import React from 'react';
import { X, Info } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { DjayAvatar, useDjaySelection } from '../../mascot';
import { soundManager } from '../../utils/soundManager';
import { hapticManager } from '../../utils/hapticManager';
import { MODE_THEME, MODE_ICON, type ModeId } from '../shared/modeTheme';
import { CenteredModal } from '../shared/CenteredModal';
import { MODE_INTRO_CONTENT } from './modeIntroContent';
interface ModeIntroSheetProps {
  isOpen: boolean;
  modeId: ModeId;
  onStart: () => void;
  onCancel: () => void;
  /** Only used by fluency to show the cold-start banner */
  isColdStart?: boolean;
}
/**
 * ModeIntroSheet — Shared intro modal for all practice modes.
 * Replaces the 4 separate intro modals (FluencyIntro, SprintIntro, etc.)
 * Part of the Learning Lab v1.3 Wave C structural refactor (F-CODE-1).
 */
export function ModeIntroSheet({
  isOpen,
  modeId,
  onStart,
  onCancel,
  isColdStart
}: ModeIntroSheetProps) {
  const {
    selectedVariant
  } = useDjaySelection();
  const theme = MODE_THEME[modeId];
  const content = MODE_INTRO_CONTENT[modeId];
  const HeaderIcon = MODE_ICON[modeId];
  return <CenteredModal isOpen={isOpen} onClose={onCancel} maxWidth="md" data-id="element-2711">
      {/* Header */}
      <div className={`bg-gradient-to-r ${theme.gradient} p-6 text-white text-center relative`} data-id="element-2712">
        <button onClick={onCancel} className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors" aria-label="Sluiten" data-id="element-2713">
          <X className="w-5 h-5" data-id="element-2714" />
        </button>
        <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3 backdrop-blur-md" data-id="element-2715">
          <HeaderIcon className={`w-8 h-8 text-white ${modeId === 'fluency' ? 'fill-white' : ''}`} data-id="element-2716" />
        </div>
        <h2 className="text-2xl font-black tracking-tight" data-id="element-2717">{theme.title}</h2>
      </div>

      {/* Content */}
      <div className="p-6 sm:p-8" data-id="element-2718">
        <div className="flex gap-4 items-start mb-6" data-id="element-2719">
          <div className="flex-shrink-0 mt-1" data-id="element-2720">
            <DjayAvatar size="md" emotion="excited" variant={selectedVariant} animate={true} data-id="element-2721" />
          </div>
          <div className={`${theme.bgSoft} rounded-2xl rounded-tl-none p-4 border ${theme.borderSoft} ${theme.textDark} text-sm font-medium leading-relaxed`} data-id="element-2722">
            {content.djayMessage}
          </div>
        </div>

        {/* Cold-start info banner (Fluency only) */}
        {isColdStart && modeId === 'fluency' && <div className="flex items-start gap-3 p-3 mb-6 bg-amber-50 border border-amber-200 rounded-xl" data-id="element-2723">
            <div className="mt-0.5 bg-amber-100 p-1 rounded-full text-amber-600 flex-shrink-0" data-id="element-2724">
              <Info className="w-4 h-4" data-id="element-2725" />
            </div>
            <p className="text-xs text-amber-800 font-medium leading-relaxed" data-id="element-2726">
              Snel & Zeker werkt het beste als je al wat geoefend hebt. Nu krijg
              je een goede startset om mee te beginnen!
            </p>
          </div>}

        <div className="space-y-4 mb-8" data-id="element-2727">
          {content.features.map((feature, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-2728">
              <div className={`mt-0.5 p-1 rounded-full ${feature.iconBgClass} ${feature.iconColorClass}`} data-id="element-2729">
                <feature.icon className="w-4 h-4" data-id="element-2730" />
              </div>
              <div data-id="element-2731">
                <p className="text-sm font-bold text-slate-800" data-id="element-2732">
                  {feature.title}
                </p>
                <p className="text-xs text-slate-500" data-id="element-2733">{feature.description}</p>
              </div>
            </div>)}
        </div>

        <div className="flex gap-3" data-id="element-2734">
          <Button variant="secondary" onClick={() => {
          soundManager.playClick();
          hapticManager.hapticLight();
          onCancel(); // F-FLOW-2: "Niet nu" dismisses without starting
        }} className="flex-1" data-id="element-2735">
            Niet nu
          </Button>
          <Button variant="primary" onClick={() => {
          soundManager.playNavigate?.() || soundManager.playClick();
          hapticManager.hapticMedium();
          onStart();
        }} className={`flex-1 bg-gradient-to-r ${theme.gradient} hover:brightness-110 border-none shadow-lg ${theme.shadow}`} data-id="element-2736">
            {content.primaryCta}
          </Button>
        </div>
      </div>
    </CenteredModal>;
}